import { AspectRatio, Box } from '@chakra-ui/react';
import { Kitchen3d, Kitchen3dController as TdController, RENDERING_CAPTURE_ASPECT } from '@rta/rta-blueprint-3d-component';
import { useCallback, useLayoutEffect, useRef, useState, type JSX } from 'react';

/** Captures a still from the previewed engine. Resolves null when the engine is not ready. */
export type PreviewCapture = (pixelRatio?: number) => Promise<string | null>;

export type RenderingPreviewSurfaceProps = {
	tdController: TdController | null;
	active: boolean;
	/** Receives a capture function bound to the previewed engine, or null when it goes away. */
	onCaptureReady?: (capture: PreviewCapture | null) => void;
};

/**
 * The single preview surface for every rendering box in the app, locked to the capture aspect.
 *
 * A controller has one engine slot (`Kitchen3dController#threeInstanceRef`) — camera tools, resize
 * handling and reset_view all resolve through it by identity, not by which component asked. Mounting
 * a second `Kitchen3d` on a controller that already has a live owning engine (the main 3D viewport)
 * does not stay inert just because it passes `ownsController={false}`: its own resize/rebuild
 * lifecycle still calls back into the *shared* controller (e.g. `controller.executeTool('reset_view')`
 * after its own resize settles), which reaches through the single slot into whichever engine owns it —
 * the main viewport. That corrupted the main view's autozoom/camera whenever a preview box was open
 * and something (e.g. navigation) resized it. This is the regression this component now avoids by
 * construction: never mount a second engine while one already owns the slot.
 *
 * - If the controller already has a live owning engine (`getCanvasElement()` non-null — the main 3D
 *   view is mounted), reuse it: mirror its on-screen canvas into a plain `<canvas>` each frame
 *   (`preserveDrawingBuffer` keeps the source readable for this) and capture through
 *   `controller.getLiveImageRender()`, which renders from that same engine without touching
 *   `controller.setImage`. No second engine, so nothing to conflict with — and "what's on screen is
 *   what you capture" is arguably the more honest contract when the main view is what the user framed.
 * - Otherwise (no live main view — e.g. the 2D view is active) there is nothing to reuse, so this
 *   mounts its own non-owning `Kitchen3d` as before, and captures through its own `instanceRef`
 *   adapter. It never claims the controller's engine slot and never writes `controller.setImage`.
 */
export const RenderingPreviewSurface = (props: RenderingPreviewSurfaceProps): JSX.Element => {
	const { tdController, active, onCaptureReady } = props;

	const [hasLiveEngine, setHasLiveEngine] = useState(false);
	const canvasRef = useRef<HTMLCanvasElement>(null);

	// Consumers (RenderingCaptureBox, RenderingPropertiesPanel) pass an inline `onCaptureReady`
	// callback that gets a new identity every render. Reading it via a ref — instead of putting it
	// in a dependency array — means calling it (which triggers the parent's setState) cannot re-fire
	// the effect that called it: that combination is an infinite loop (effect calls callback -> parent
	// re-renders -> new callback identity -> effect dep changed -> effect re-fires -> ...).
	const onCaptureReadyRef = useRef(onCaptureReady);
	onCaptureReadyRef.current = onCaptureReady;

	// No event exists for "an owning engine mounted/unmounted on this controller", so poll while the
	// box is open — cheap, and reacts to the user navigating in/out of the 3D view while it's open.
	useLayoutEffect(() => {
		if (!active || !tdController) { setHasLiveEngine(false); return; }

		const check = (): void => setHasLiveEngine(!!tdController.getCanvasElement?.());
		check();
		const intervalId = window.setInterval(check, 400);
		return () => window.clearInterval(intervalId);
	}, [active, tdController]);

	// Reuse path: mirror the main engine's live canvas and hand out a capture bound to it.
	useLayoutEffect(() => {
		if (!active || !tdController || !hasLiveEngine) return;

		let rafId = 0;
		const draw = (): void => {
			const src = tdController.getCanvasElement?.();
			const dst = canvasRef.current;
			if (src && dst && src.width > 0 && src.height > 0) {
				const cssWidth = dst.clientWidth || src.width;
				const cssHeight = dst.clientHeight || src.height;
				if (dst.width !== cssWidth || dst.height !== cssHeight) {
					dst.width = cssWidth;
					dst.height = cssHeight;
				}
				const ctx = dst.getContext('2d');
				if (ctx) {
					const scale = Math.min(cssWidth / src.width, cssHeight / src.height);
					const w = src.width * scale;
					const h = src.height * scale;
					ctx.fillStyle = '#1A202C';
					ctx.fillRect(0, 0, cssWidth, cssHeight);
					ctx.drawImage(src, (cssWidth - w) / 2, (cssHeight - h) / 2, w, h);
				}
			}
			rafId = requestAnimationFrame(draw);
		};
		rafId = requestAnimationFrame(draw);

		onCaptureReadyRef.current?.((pixelRatio = 2) => tdController.getLiveImageRender?.(pixelRatio) ?? Promise.resolve(null));

		return () => {
			cancelAnimationFrame(rafId);
			onCaptureReadyRef.current?.(null);
		};
	}, [active, tdController, hasLiveEngine]);

	// Private-mount path: no live main engine to reuse, so mount our own non-owning instance.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const instanceRef = useRef<any>(null);
	const handleInstance = useCallback((instance: unknown | null): void => {
		instanceRef.current = instance;
		if (!onCaptureReady) return;
		if (!instance) {
			onCaptureReady(null);
			return;
		}
		onCaptureReady((pixelRatio = 2) => new Promise<string | null>((resolve) => {
			const current = instanceRef.current;
			if (!current?.getImageRender) { resolve(null); return; }
			current.getImageRender(pixelRatio, (imageData: string) => resolve(imageData || null));
		}));
	}, [onCaptureReady]);

	return <AspectRatio ratio={RENDERING_CAPTURE_ASPECT} borderRadius="md" overflow="hidden" bg="gray.900" w="100%">
		<Box>
			{active && tdController && (
				hasLiveEngine
					? <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
					: <Kitchen3d
						controller={tdController}
						backgroundColor="#1A202C"
						instanceRef={handleInstance}
						ownsController={false}
					/>
			)}
		</Box>
	</AspectRatio>;
};
